<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const adjustments = [
  {
    id: 'CA-2024-00089',
    project: 'Solar Park Beta – Northern Highlands',
    country: 'Kenya',
    issuanceRef: 'CCI-2024-00089-001',
    creditsIssued: 6120,
    caAmount: 6120,
    caApplied: '2024-12-15',
    caAuthority: 'National Climate Change Authority, Kenya',
    status: 'Confirmed',
    statusColor: 'green',
    biennial: 'BR-2025',
    notes: 'Corresponding adjustment confirmed in Kenya national registry. Reference: KE-CA-2024-089.',
  },
  {
    id: 'CA-2025-TBD',
    project: 'Mangrove Restoration Initiative – Borneo',
    country: 'Indonesia',
    issuanceRef: 'Pending issuance',
    creditsIssued: 0,
    caAmount: 0,
    caApplied: '—',
    caAuthority: 'Ministry of Environment and Forestry, Indonesia',
    status: 'Pending Issuance',
    statusColor: 'gray',
    biennial: '—',
    notes: 'Project not yet issued credits. Corresponding adjustment will be required upon first issuance.',
  },
]

const statusClass = (color: string) => {
  const map: Record<string, string> = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-100 text-red-800',
  }
  return map[color] ?? 'bg-gray-100 text-gray-600'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Corresponding Adjustments</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">Corresponding Adjustments Status</h1>
            <p class="text-gray-600">
              Under Article 6.4, the host country must apply a corresponding adjustment (CA) to any Internationally Transferred Mitigation Outcomes (ITMOs). This page shows the CA status for your issued credits.
            </p>
          </div>
        </section>

        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <p class="text-sm font-medium text-blue-800 mb-1">
            <i class="fas fa-info-circle mr-2"></i>Why Corresponding Adjustments Matter
          </p>
          <p class="text-sm text-blue-700">
            Without a confirmed corresponding adjustment, credits transferred internationally cannot be counted by the buyer as a contribution to their NDC or under Article 6.4. The host country's CA confirms they have reduced their own NDC by the equivalent amount.
          </p>
        </div>

        <section class="space-y-6">
          <div v-for="adj in adjustments" :key="adj.id" class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between mb-4">
              <div>
                <div class="flex items-center space-x-3 mb-1">
                  <h3 class="font-semibold text-accent">{{ adj.project }}</h3>
                  <span :class="['inline-flex px-2 py-1 rounded-full text-xs font-medium', statusClass(adj.statusColor)]">
                    {{ adj.status }}
                  </span>
                </div>
                <p class="text-sm text-gray-500">CA Ref: {{ adj.id }} &nbsp;·&nbsp; Issuance: {{ adj.issuanceRef }}</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
              <div><p class="text-gray-500">Host Country</p><p class="font-medium text-gray-800">{{ adj.country }}</p></div>
              <div><p class="text-gray-500">CA Authority</p><p class="font-medium text-gray-800">{{ adj.caAuthority }}</p></div>
              <div><p class="text-gray-500">CA Applied Date</p><p class="font-medium text-gray-800">{{ adj.caApplied }}</p></div>
              <div><p class="text-gray-500">Credits Issued</p><p class="font-medium text-gray-800">{{ adj.creditsIssued.toLocaleString() }} tCO2e</p></div>
              <div><p class="text-gray-500">CA Amount</p><p class="font-medium text-gray-800">{{ adj.caAmount.toLocaleString() }} tCO2e</p></div>
              <div><p class="text-gray-500">Biennial Report Ref</p><p class="font-medium text-gray-800">{{ adj.biennial }}</p></div>
            </div>

            <div class="bg-gray-50 rounded-md px-4 py-3 text-sm text-gray-700 mb-4">
              <i class="fas fa-comment-alt text-gray-400 mr-2"></i>{{ adj.notes }}
            </div>

            <div class="flex items-center space-x-3">
              <button v-if="adj.statusColor === 'green'" class="px-4 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">
                <i class="fas fa-download mr-1"></i>Download CA Confirmation
              </button>
              <button v-if="adj.statusColor === 'yellow'" class="px-4 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                <i class="fas fa-paper-plane mr-1"></i>Follow Up with DNA
              </button>
            </div>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
